import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approve_deletion,
  cancel_deletion,
  fetch_deletion_counts,
  fetch_deletion_detail,
  fetch_deletion_list,
  reject_deletion,
} from './api';
import type { AccountDeletionFilters } from './types';
import type { SortState } from '@/hooks/use-url-state';

const KEYS = {
  list: (filters: AccountDeletionFilters) => ['admin-account-deletions', filters] as const,
  detail: (id: string) => ['admin-account-deletion', id] as const,
  counts: ['admin-account-deletion-counts'] as const,
};

export function use_deletion_list(filters: AccountDeletionFilters, sort?: SortState) {
  return useQuery({
    queryKey: [...KEYS.list(filters), sort],
    queryFn: () => fetch_deletion_list(filters, sort),
    placeholderData: (prev) => prev,
  });
}

export function use_deletion_counts() {
  return useQuery({ queryKey: KEYS.counts, queryFn: fetch_deletion_counts });
}

export function use_deletion_detail(id: string | null) {
  return useQuery({
    queryKey: id ? KEYS.detail(id) : ['admin-account-deletion', 'noop'],
    queryFn: () => fetch_deletion_detail(id as string),
    enabled: typeof id === 'string' && id.length > 0,
  });
}

/**
 * The three decisions.
 *
 * Every one of them invalidates the tiles as well as the list: approving is the only action
 * in this console that deactivates a live account, and a queue count that still says "3
 * pending" after you cleared one is the kind of thing that gets an account closed twice.
 */
export function use_deletion_actions(id: string | null) {
  const qc = useQueryClient();
  const invalidate = () => {
    if (id) qc.invalidateQueries({ queryKey: KEYS.detail(id) });
    qc.invalidateQueries({ queryKey: ['admin-account-deletions'] });
    qc.invalidateQueries({ queryKey: KEYS.counts });
    // The sidebar badge reads the dashboard stats, not this module's counts.
    qc.invalidateQueries({ queryKey: ['admin-stats'] });
  };

  const approve = useMutation({
    mutationFn: (note?: string) => approve_deletion(id as string, note),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: (reason: string) => reject_deletion(id as string, reason),
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: (reason: string) => cancel_deletion(id as string, reason),
    onSuccess: invalidate,
  });

  return { approve, reject, cancel };
}
